import {
  GridColDef,
  GridColumns,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-premium';
import { dateFilterOperators, filterOperators, numberFilterOperators } from '../../../../constants';
import { useTranslation } from 'react-i18next';
import { baseCell } from 'components/common/enhanced/cells/base-cell';
import DataGridCell from 'components/common/DataGridCell';
import { currencyFormatter } from 'helpers/currency-formatter';
import { dateTimeCell } from '../cells/date-time-cell';
import DateTimeUpdate from 'components/common/HistoryList/handleDateTimeUpdate';
import ConfirmationDeleteTableDialog from 'components/helpers/ConfirmationGridDelete';
import dayjs from 'dayjs';
import { isTenantAdministrator } from 'components/helpers/IsTenantAdministrator';

export const ExamHistoryLineColumns = (
  handleDateTimeUpdate: any,
  handleDelete: any,
  hideFields: boolean,
  tagOptions?: string[],
  macroExamOptions?: string[]
): GridColDef[] => {
  const translationPrefix = 'entities.exam';

  const { t } = useTranslation();
  const columns: GridColumns = [];

  columns.push(
    {
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        const actions = [];
        if (isTenantAdministrator()) {
          actions.push(
            <DateTimeUpdate key={980} id={params.row['id']} handleUpdate={handleDateTimeUpdate} />
          );
          actions.push(
            <ConfirmationDeleteTableDialog handleDelete={handleDelete} id={params.row.id} />
          );
        }

        return actions;
      }
    },
    {
      field: 'exam',
      headerName: t(`${translationPrefix}.history.exam`),
      width: 100,
      filterOperators,
      editable: false,
      renderCell: baseCell
    },
    {
      field: 'datetime',
      headerName: t(`${translationPrefix}.history.datetime`),
      width: 150,
      filterOperators: dateFilterOperators,
      renderCell: dateTimeCell,
      valueFormatter: (params) => {
        return params.value ? dayjs(params.value).format('YYYY-MM-DD HH:MM:00') : '';
      }
    },
    {
      field: 'category',
      headerName: t(`${translationPrefix}.category`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'regional_rate_snapshot_code',
      headerName: t(`${translationPrefix}.regional_rate_snapshot_code`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    }
  );

  [
    'regional_rate_snapshot_rate',
    'contractual_rate',
    'rate_1',
    'rate_2',
    'rate_3',
    'rate_4',
    'rate_5'
  ].forEach((field) => {
    columns.push({
      field: field,
      width: 150,
      headerName: t(`${translationPrefix}.${field}`),
      filterOperators: numberFilterOperators,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        const price = params.value;
        return <DataGridCell content={currencyFormatter(price)} />;
      }
    });
  });

  ['extra_1', 'extra_2', 'extra_3', 'extra_4', 'extra_5'].forEach((field) => {
    columns.push({
      field: field,
      width: 200,
      headerName: t(`${translationPrefix}.${field}`),
      filterOperators,
      sortable: true,
      renderCell: baseCell
    });
  });

  // Tag column with select filter
  const tagColumn: GridColDef = {
    field: 'tag',
    width: 125,
    headerName: t(`${translationPrefix}.history.tag`),
    sortable: true,
    filterable: true,
    renderCell: baseCell
  };

  if (tagOptions && tagOptions.length > 0) {
    tagColumn.type = 'singleSelect';
    tagColumn.valueOptions = tagOptions;
  } else {
    tagColumn.filterOperators = filterOperators;
  }

  columns.push(tagColumn);

  // Macro exam column with select filter
  const macroExamColumn: GridColDef = {
    field: 'macro_exam',
    width: 125,
    headerName: t(`${translationPrefix}.history.macro_exam`),
    sortable: true,
    renderCell: baseCell
  };

  if (macroExamOptions && macroExamOptions.length > 0) {
    macroExamColumn.type = 'singleSelect';
    macroExamColumn.valueOptions = macroExamOptions;
  } else {
    macroExamColumn.filterOperators = filterOperators;
  }

  columns.push(macroExamColumn);

  if (!hideFields) {
    ['products_snapshot', 'equipments_snapshot', 'workstations_snapshot'].forEach((field) => {
      columns.push({
        field: field,
        width: 150,
        headerName: t(`${translationPrefix}.history.${field}`),
        sortable: false,
        valueFormatter: ({ value }) => {
          const values = value.map((value) => value.code);
          return values.join(', ');
        },
        filterOperators: filterOperators,
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value.map((value) => value['code']);
          return <DataGridCell content={value ? value.join(',') : ''} />;
        }
      });
    });
  }

  return columns;
};
